import * as React from 'react';
import {none, option, Option} from "ts-option";
import {FocusableProps} from "../../../../components/common/props/FocusableProps";
import {ClickableProps} from "../../../../components/common/props/ClickableProps";
import {EditableProps} from "../../../../components/common/props/EditableProps";
import {DisableableProps} from "../../../../components/common/props/DisableableProps";
import {PressableProps} from "../../../../components/common/props/PresableProps";
import {PlaceholderProps} from "../../../../components/common/props/PlaceholderProps";
import Input from './Input';
import NumberUtils from "../../../../utils/NumberUtils";
import {isDefined} from "../../../../utils/Utils";

interface ComponentProps {
	/**
	 * Input change callback.
	 */
	onChange: (value: Option<number>) => void;
	/**
	 * Input value.
	 */
	value?: Option<number>;
	/**
	 * Max value.
	 */
	maxValue?: Option<number>;
	/**
	 * Min value.
	 */
	minValue?: Option<number>;
	/**
	 * Max number of decimal places allowed.
	 */
	maxDecimalPlaces?: number;
}

type Props =
	ComponentProps
	& FocusableProps
	& ClickableProps
	& EditableProps
	& DisableableProps
	& PressableProps
	& PlaceholderProps

export default class RangeNumberInput extends React.Component<Props, {}> {
	public static defaultProps: Partial<Props> = {
		value: none,
		minValue: none,
		maxValue: none
	};

	constructor(props: Props) {
		super(props);
		this.onChange = this.onChange.bind(this);
		this.testRange = this.testRange.bind(this);
		this.testMaxDecimalPlaces = this.testMaxDecimalPlaces.bind(this);
	}

	render() {
		const value = this.props.value.isDefined ? this.props.value.get : "";
		return (
			<Input onChange={this.onChange}
				   onClick={this.props.onClick}
				   focus={this.props.focus}
				   onFocus={this.props.onFocus}
				   onConfirm={this.props.onEnter}
				   type={"number"}
				   placeholder={this.props.placeholder}
				   value={value}
				   readonly={this.props.readonly}
				   disabled={this.props.disabled}/>
		);
	}

	/**
	 * On change action.
	 */
	onChange(evt: React.ChangeEvent<HTMLInputElement>) {
		if (!evt.target.value) {
			this.props.onChange(none);
		}
		else {
			const number = Number(evt.target.value);
			if (!isNaN(number) && this.testRange(number) && this.testMaxDecimalPlaces(number)) {
				this.props.onChange(option(number));
			}
		}
	}

	testRange(value: number): boolean {
		let moreOrEqualToMin = true;
		let lessOrEqualToMax = true;
		if (this.props.minValue.isDefined) {
			moreOrEqualToMin = value >= this.props.minValue.get
		}
		if (this.props.maxValue.isDefined) {
			lessOrEqualToMax = value <= this.props.maxValue.get
		}
		return moreOrEqualToMin && lessOrEqualToMax;
	}

	testMaxDecimalPlaces(value: number): boolean {
		if (isDefined(this.props.maxDecimalPlaces)) {
			return NumberUtils.getNumberOfDecimals(value) <= this.props.maxDecimalPlaces;
		}
		return true;
	}
}